import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, onSnapshot, addDoc, Timestamp, orderBy, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import './Transfer.css';

interface Withdrawal {
  id: string;
  amount: number;
  createdAt: Timestamp | number;
  walletAddress?: string;
}

export default function Transfer() {
  const { colors } = useTheme();
  const [pixBalance, setPixBalance] = useState<number>(0);
  const [wallet, setWallet] = useState<string>('');
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const userRef = doc(db, 'users', currentUser.uid);
    
    // Listen for user data changes
    const unsubscribeUser = onSnapshot(userRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setWallet(data.walletAddress || '');

        // Calculate PIX balance
        const completedTasks = data?.completedTasks || {};
        let totalEarned = 0;
        
        Object.values(completedTasks).forEach((task: any) => {
          const submissions = Array.isArray(task.submissions) ? task.submissions : [task];
          submissions.forEach((sub: any) => {
            if (sub.status === 'approved') {
              const reward = Number(sub.reward) || Number(sub.rewardAmount) || 0;
              totalEarned += reward;
            }
          });
        });
        
        // Get withdrawals
        const withdrawalsRef = collection(db, 'users', currentUser.uid, 'withdrawals');
        const withdrawalsQuery = query(withdrawalsRef, orderBy('createdAt', 'desc'));
        const withdrawalsSnap = await getDocs(withdrawalsQuery);
        const withdrawalsList: Withdrawal[] = [];
        let calculatedWithdrawn = 0;
        
        withdrawalsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.amount) {
            withdrawalsList.push({
              id: doc.id,
              amount: data.amount,
              createdAt: data.createdAt,
              walletAddress: data.walletAddress || wallet
            });
            calculatedWithdrawn += data.amount;
          }
        });
        
        setWithdrawals(withdrawalsList);
        setPixBalance(totalEarned - calculatedWithdrawn);
        setLoading(false);
      }
    });

    // Listen for withdrawals changes
    const withdrawalsRef = collection(db, 'users', currentUser.uid, 'withdrawals');
    const withdrawalsQuery = query(withdrawalsRef, orderBy('createdAt', 'desc'));
    const unsubscribeWithdrawals = onSnapshot(withdrawalsQuery, async (snapshot) => {
      const withdrawalsList: Withdrawal[] = [];
      let calculatedWithdrawn = 0;
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.amount) {
          withdrawalsList.push({
            id: doc.id,
            amount: data.amount,
            createdAt: data.createdAt,
            walletAddress: data.walletAddress || wallet
          });
          calculatedWithdrawn += data.amount;
        }
      });
      
      setWithdrawals(withdrawalsList);
      
      // Recalculate balance when withdrawals change
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      if (userSnap.exists()) {
        const data = userSnap.data();
        const completedTasks = data?.completedTasks || {};
        let totalEarned = 0;
        
        Object.values(completedTasks).forEach((task: any) => {
          const submissions = Array.isArray(task.submissions) ? task.submissions : [task];
          submissions.forEach((sub: any) => {
            if (sub.status === 'approved') {
              const reward = Number(sub.reward) || Number(sub.rewardAmount) || 0;
              totalEarned += reward;
            }
          });
        });
        
        setPixBalance(totalEarned - calculatedWithdrawn);
      }
    });

    return () => {
      unsubscribeUser();
      unsubscribeWithdrawals();
    };
  }, [wallet]);

  const handleTransfer = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setError(null);
    const transferAmount = Number(amount);

    // Validation
    if (!transferAmount || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (transferAmount > pixBalance) {
      setError('Insufficient balance. You do not have enough PIX.');
      return;
    }

    if (!wallet) {
      setError('Please connect a wallet first. Go to Profile to connect your wallet.');
      return;
    }

    setTransferring(true);

    try {
      // Add withdrawal to Firestore (UI only - no actual wallet integration yet)
      await addDoc(collection(db, 'users', currentUser.uid, 'withdrawals'), {
        amount: transferAmount,
        createdAt: Timestamp.now(),
        walletAddress: wallet,
        status: 'pending' // UI only - pending actual wallet integration
      });

      // Clear form
      setAmount('');
      setError(null);
      alert(`Transfer recorded! ${transferAmount} PIX will be transferred to your wallet (UI demo - wallet integration coming soon).`);
    } catch (error: any) {
      console.error('Error transferring PIX:', error);
      setError('Failed to transfer PIX: ' + error.message);
    } finally {
      setTransferring(false);
    }
  };

  const formatDate = (timestamp: Timestamp | number | undefined): string => {
    if (!timestamp) return '';
    const ms = (timestamp && typeof timestamp === 'object' && 'toMillis' in timestamp)
      ? timestamp.toMillis()
      : (typeof timestamp === 'number' ? timestamp : 0);
    if (ms === 0) return '';
    const date = new Date(ms);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="transfer-page" style={{ background: colors.background, color: colors.text }}>
        <div className="transfer-container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="transfer-page" style={{ background: colors.background, color: colors.text }}>
      <div className="transfer-container">
        <h1 style={{ color: colors.text }}>Transfer $PIX</h1>

        {/* Balance & Transfer Section */}
        <div className="transfer-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>In-App Balance</h2>
          <div className="balance-display">
            <div className="balance-value" style={{ color: colors.primary }}>
              {pixBalance.toFixed(0)} $PIX
            </div>
            <div className="balance-label" style={{ color: colors.text }}>
              Available to transfer
            </div>
          </div>

          {!wallet && (
            <div className="wallet-warning" style={{ background: colors.warning + '20', borderColor: colors.warning }}>
              <p style={{ color: colors.text }}>
                ⚠️ No wallet connected. Please connect a wallet in your Profile settings to transfer PIX.
              </p>
            </div>
          )}

          {wallet && (
            <>
              <div className="wallet-info" style={{ color: colors.text }}>
                <span className="wallet-label">Connected Wallet:</span>
                <span className="wallet-address" style={{ color: colors.primary }}>{wallet}</span>
              </div>

              <div className="transfer-form">
                <label style={{ color: colors.text }}>Transfer Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter amount"
                  min="1"
                  max={pixBalance}
                  style={{ 
                    background: colors.background, 
                    color: colors.text, 
                    borderColor: colors.border 
                  }}
                  disabled={transferring}
                />
                {error && (
                  <div className="error-message" style={{ color: colors.error }}>
                    {error}
                  </div>
                )}
                <button
                  className="transfer-button"
                  style={{ background: colors.primary }}
                  onClick={handleTransfer}
                  disabled={transferring || !amount || Number(amount) <= 0 || Number(amount) > pixBalance}
                >
                  {transferring ? 'Transferring...' : 'Transfer to Wallet'}
                </button>
                <div className="transfer-hint" style={{ color: colors.secondary }}>
                  Enter the amount of PIX you want to transfer to your connected wallet.
                </div>
              </div>
            </>
          )}
        </div>

        {/* Transfer History Section */}
        <div className="transfer-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Transfer History</h2>
          {withdrawals.length === 0 ? (
            <div className="empty-state" style={{ color: colors.secondary }}>
              No transfers yet. Transfer PIX to your wallet to see history here.
            </div>
          ) : (
            <div className="history-list">
              {withdrawals.map((withdrawal) => (
                <div 
                  key={withdrawal.id} 
                  className="history-item"
                  style={{ borderColor: colors.border }}
                >
                  <div className="history-main">
                    <div className="history-amount" style={{ color: colors.error }}>
                      -{withdrawal.amount.toFixed(0)} $PIX
                    </div>
                    <div className="history-date" style={{ color: colors.secondary }}>
                      {formatDate(withdrawal.createdAt)}
                    </div>
                  </div>
                  {withdrawal.walletAddress && (
                    <div className="history-wallet" style={{ color: colors.secondary }}>
                      {withdrawal.walletAddress}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

